import { URL } from "../../utils/UrlConstants";
import { privateFetch } from "../../utils/CustomFetch.js";
import { CONSTANT } from "../../utils/Constants";

export interface VacationListItem {
  personId: number;
  fullName: string;
  dni: string;
  positionName: string;
  availableDays: number;
  paymentType: string;
  state: string;
}

export interface VacationRecord {
  id: number;
  startDate: string;
  endDate: string;
  days: number;
  daysToDeduct: number;
  type: string;
  description: string | null;
}

export interface VacationDetail {
  personId: number;
  fullName: string;
  dni: string;
  positionName: string;
  paymentType: string;
  monthlySalary: number;
  earnedDays: number;
  accumulatedDays: number;
  enjoyedDays: number;
  availableDays: number;
  records: VacationRecord[];
}

export interface VacationMonthly {
  year: number;
  month: number;
  monthLabel: string;
  salary: number;
  daysWorked: number;
}

export interface VacationPayment {
  personId: number;
  fullName: string;
  positionName: string;
  paymentType: string;
  months: VacationMonthly[];
  totalSalary12Months: number;
  totalDaysWorked: number;
  dailySalaryAverage: number;
  vacationDays: number;
  subtotalVacations: number;
  ccssDeduction: number;
  totalToPay: number;
  vacationDescription: string;
}

export interface VacationRecordSavePayload {
  personId: number;
  startDate: string;
  endDate: string;
  type: string;
  daysToDeduct: number;
  description?: string;
  user: string;
}

export const getVacationList = async (
  filter?: string
): Promise<VacationListItem[]> => {
  try {
    const params: Record<string, string> = {};
    if (filter && filter.trim().length > 0) {
      params.filter = filter.trim();
    }
    const response = await privateFetch.get(URL.VACATION_LIST, { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ?? error?.message ?? "Error al cargar vacaciones"
    );
  }
};

export const getVacationDetail = async (
  personId: number
): Promise<VacationDetail> => {
  try {
    const response = await privateFetch.get(URL.VACATION_DETAIL(personId));
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ?? error?.message ?? "Error al cargar detalle de vacaciones"
    );
  }
};

export const getVacationPayment = async (
  personId: number,
  vacationRecordId: number
): Promise<VacationPayment> => {
  try {
    const response = await privateFetch.get(URL.VACATION_PAYMENT(personId), {
      params: { vacationRecordId },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ?? error?.message ?? "Error al calcular pago de vacaciones"
    );
  }
};

export const saveVacationRecord = async (
  payload: VacationRecordSavePayload
): Promise<void> => {
  try {
    await privateFetch.post(
      URL.VACATION_RECORD,
      JSON.stringify(payload),
      { headers: { ...CONSTANT.HEADER_TYPE } }
    );
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message
        ? error.response.data.message
        : "No se pudo guardar el registro de vacaciones"
    );
  }
};

export const deleteVacationRecord = async (id: number): Promise<void> => {
  try {
    await privateFetch.delete(URL.VACATION_RECORD_DELETE(id));
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message
        ? error.response.data.message
        : "No se pudo eliminar el registro de vacaciones"
    );
  }
};

export interface VacationImportError {
  sheet: string;
  row: number;
  fullName: string | null;
  message: string;
}

export interface VacationImportResult {
  totalRows: number;
  imported: number;
  operariosImported: number;
  administrativosImported: number;
  monthlyRowsImported: number;
  errors: VacationImportError[];
  warnings: VacationImportError[];
}

export const importVacationExcel = async (
  file: File,
  user: string
): Promise<VacationImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (user) formData.append("user", user);
    const response = await privateFetch.post(URL.VACATION_IMPORT, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message
        ? error.response.data.message
        : "No se pudo importar el Excel de saldo inicial"
    );
  }
};

/**
 * Fechas (YYYY-MM-DD) en las que el colaborador está de vacaciones dentro del
 * rango. La usan Avances y Salarios Administrativos para pintar y bloquear
 * esos días.
 *
 * Devuelve una lista vacía si la consulta falla: no poder marcar el día no
 * debe romper la pantalla de carga de horas.
 */
export const getVacationDays = async (
  personId: number,
  from: string,
  to: string
): Promise<string[]> => {
  try {
    const response = await privateFetch.get(URL.VACATION_DAYS, {
      params: { personId, from, to },
    });
    return response.data ?? [];
  } catch (error: any) {
    console.error("Error al consultar los días de vacaciones:", error);
    return [];
  }
};
